/**
 * Simple variable detection for uppercase variables with underscores
 * Detects variables like SYSTEM_PROMPT = "value" and allows modification
 */

/**
 * Detects uppercase variables with underscores in JavaScript/JSX code
 * @param {string} code - The source code to analyze
 * @returns {Array} Array of detected variables with their values and positions
 */
// Global variables injected by the component writer — not user-editable
// Global variables injected by the component writer — not user-editable
const GLOBAL_VARIABLE_NAMES = new Set([
    "API_KEY",
    "APP_URL",
    "NODE_ENV",
    "PORT",
    "INTEGRATIONS",
    "DATABASE_PROXY",
    "PROXY_INTEGRATION_URL",
    "TELEMETRY_URL",
]);

export function detectUppercaseVariables(code) {
    if (!code || typeof code !== "string") {
        return [];
    }

    const variables = [];

    // Handle template literals and multiline strings
    const templateLiteralRegex =
        /^(\s*)(const\s+|let\s+|var\s+)?([A-Z][A-Z0-9_]*)\s*=\s*`([\s\S]*?)`;?/gm;
    const singleLineRegex =
        /^(\s*)(const\s+|let\s+|var\s+)?([A-Z][A-Z0-9_]*)\s*=\s*(['"])(.*?)\4;?/gm;

    let match;

    // Find template literals first
    while ((match = templateLiteralRegex.exec(code)) !== null) {
        const [fullMatch, indent, declaration, varName, value] = match;
        const startIndex = match.index;
        const beforeMatch = code.substring(0, startIndex);
        const lineNumber = beforeMatch.split("\n").length;

        variables.push({
            name: varName,
            value: value,
            quote: "`",
            valueType: "template",
            line: lineNumber,
            indent: indent || "",
            fullLine: fullMatch.trim(),
            isMultiline: value.includes("\n"),
        });
    }

    // Find single line string literals
    while ((match = singleLineRegex.exec(code)) !== null) {
        const [fullMatch, indent, declaration, varName, quote, value] = match;
        const startIndex = match.index;
        const beforeMatch = code.substring(0, startIndex);
        const lineNumber = beforeMatch.split("\n").length;

        // Skip if this variable was already found as a template literal
        if (!variables.find((v) => v.name === varName)) {
            variables.push({
                name: varName,
                value: value,
                quote: quote,
                valueType:
                    quote === "`"
                        ? "template"
                        : quote === '"'
                        ? "string"
                        : "string",
                line: lineNumber,
                indent: indent || "",
                fullLine: fullMatch.trim(),
                isMultiline: false,
            });
        }
    }

    return variables
        .filter((v) => !GLOBAL_VARIABLE_NAMES.has(v.name))
        .sort((a, b) => a.line - b.line);
}

// Debouncing mechanism to prevent rapid successive updates
const updateTimestamps = new Map();
const DEBOUNCE_DELAY = 50; // 50ms debounce

/**
 * Updates a variable value in the code
 * @param {string} code - The original source code
 * @param {string} variableName - The name of the variable to update
 * @param {string} newValue - The new value to set
 * @param {boolean} skipDebounce - Skip debouncing (for testing or forced updates)
 * @returns {string} The updated code
 */
export function updateVariableValue(
    code,
    variableName,
    newValue,
    skipDebounce = false
) {
    // Debouncing logic
    if (!skipDebounce) {
        const now = Date.now();
        const key = `${variableName}:${newValue}`;
        const lastUpdate = updateTimestamps.get(key);

        if (lastUpdate && now - lastUpdate < DEBOUNCE_DELAY) {
            return code; // Skip this update, too soon
        }

        updateTimestamps.set(key, now);

        // Clean up old timestamps to prevent memory leaks
        if (updateTimestamps.size > 100) {
            const cutoffTime = now - DEBOUNCE_DELAY * 10;
            for (const [k, timestamp] of updateTimestamps.entries()) {
                if (timestamp < cutoffTime) {
                    updateTimestamps.delete(k);
                }
            }
        }
    }
    if (!code || !variableName || newValue === undefined || newValue === null) {
        return code;
    }

    const variables = detectUppercaseVariables(code);
    const targetVariable = variables.find((v) => v.name === variableName);

    if (!targetVariable) {
        return code;
    }

    // Minimal escaping - only escape when absolutely necessary
    let escapedValue = newValue;

    // Only escape if the new value contains the quote character used by the variable
    if (targetVariable.quote === "`" && newValue.includes("`")) {
        escapedValue = newValue.replace(/`/g, "\\`");
    } else if (targetVariable.quote === "`" && newValue.includes("${")) {
        escapedValue = newValue.replace(/\$\{/g, "\\${");
    } else if (
        targetVariable.quote !== "`" &&
        newValue.includes(targetVariable.quote)
    ) {
        escapedValue = newValue.replace(
            new RegExp(targetVariable.quote, "g"),
            `\\${targetVariable.quote}`
        );
    }
    // For most cases (simple strings without conflicting quotes), no escaping needed

    // Build the replacement pattern for the original variable assignment
    // Use indent from detection (preserved separately) + declaration keyword from fullLine
    const declarationMatch = targetVariable.fullLine.match(
        /^(const\s+|let\s+|var\s+)/
    );
    const declarationKeyword = declarationMatch ? declarationMatch[0] : "";

    // Create new assignment preserving original indentation
    const newAssignment = `${targetVariable.indent}${declarationKeyword}${targetVariable.name} = ${targetVariable.quote}${escapedValue}${targetVariable.quote}`;

    // For multiline variables, we need to replace the entire multiline block
    if (targetVariable.isMultiline) {
        // Use regex to find and replace the entire variable assignment including multiline content
        const escapedVarName = targetVariable.name.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&"
        );
        const variablePattern = new RegExp(
            `^(\\s*)(const\\s+|let\\s+|var\\s+)?(${escapedVarName})\\s*=\\s*\`[\\s\\S]*?\``,
            "gm"
        );

        return code.replace(variablePattern, newAssignment);
    } else {
        // For single line variables, use precise regex replacement to avoid issues
        const escapedVarName = targetVariable.name.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&"
        );
        const quoteChar = targetVariable.quote.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&"
        );
        const variablePattern = new RegExp(
            `^(\\s*)(const\\s+|let\\s+|var\\s+)?(${escapedVarName})\\s*=\\s*${quoteChar}.*?${quoteChar}`,
            "gm"
        );

        return code.replace(variablePattern, newAssignment);
    }
}

/**
 * Gets a summary of all detected variables
 * @param {string} code - The source code to analyze
 * @returns {Object} Summary object with variable counts
 */
export function getVariableSummary(code) {
    const variables = detectUppercaseVariables(code);

    return {
        total: variables.length,
        variables: variables.map((v) => ({
            name: v.name,
            value: v.value,
            line: v.line,
        })),
    };
}

/**
 * Clears the debounce cache - useful for testing or when you want to force updates
 */
export function clearUpdateDebounceCache() {
    updateTimestamps.clear();
}
